import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Spinner, Modal, Button, Form } from "react-bootstrap";
import { siteLogo as logo } from "../../const";
import Footer from "../../ComponentsWeb/Sub/Footer";
import ChatInput from "./ChatInput";
import MobileHeader from "../../ComponentsWeb/Sub/MobileHeader";
import moment from "moment";
import { socketEmit, socketOn } from "../../Socket";
import { getAudioMimeType, isAudio, isImage, socketId } from "../../const";
import socket from "../../socketInstance";
import ShowImage from "../../Components/ShowImage";
import EditMsg from "./EditMsg";
import ReplyPreview from "./ReplyPreview";
import LinkifiedText from "../../Components/LinkifiedText";
import { allApi, postApi } from "../../helpers/apiStructure";
// import messageAudioNotification from "../../images/message-notification.wav"

const Chat = ({ socketReady }) => {
  const scrollableDivRef = useRef(null);
  const messagesEndRef = useRef(null);
  const autoScrollTimeoutsRef = useRef([]);

  const [chatMessages, setChatMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chatHeight, setChatHeight] = useState("100%");
  const [replyMessage, setReplyMessage] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);
  const [forwardMessages, setForwardMessages] = useState([]);
  const [forwardUsers, setForwardUsers] = useState([]);
  const [forwardGroups, setForwardGroups] = useState([]);
  const [forwardLoading, setForwardLoading] = useState(false);
  const [forwardSearch, setForwardSearch] = useState("");
  const [forwardingTargetKey, setForwardingTargetKey] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const isScrolledUpRef = useRef(false);
  const [jumpTargetMessageId, setJumpTargetMessageId] = useState(null);
  const jumpAbortRef = useRef(false);
  const [returnMessageId, setReturnMessageId] = useState(null);

  const toggleSearch = () => {
    setShowSearch((prev) => {
      if (prev) setSearchQuery("");
      return !prev;
    });
  };

  const copyMessageText = (msg) => {
    const text = msg?.content?.trim();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      const id = msg?.id || msg?._id;
      setCopiedMessageId(id);
      setTimeout(() => setCopiedMessageId((cur) => (cur === id ? null : cur)), 2000);
    });
  };

  const isMessageSelected = (msg) => {
    const id = msg?.id || msg?._id;
    return selectedMessages.some((m) => (m?.id || m?._id) === id);
  };

  const toggleMessageSelection = (msg) => {
    const id = msg?.id || msg?._id;
    setSelectedMessages((prev) => {
      const exists = prev.some((m) => (m?.id || m?._id) === id);
      if (exists) {
        const updated = prev.filter((m) => (m?.id || m?._id) !== id);
        if (updated.length === 0) setIsSelectionMode(false);
        return updated;
      }
      return [...prev, msg];
    });
  };

  const enterSelectionMode = (msg) => {
    setIsSelectionMode(true);
    setSelectedMessages([msg]);
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedMessages([]);
  };

  const resolveReplyMessage = (replyTo) => {
    if (!replyTo) {
      return null;
    }

    if (typeof replyTo === "object" && (replyTo?.content || replyTo?.files?.length)) {
      return replyTo;
    }

    const replyId =
      typeof replyTo === "string"
        ? replyTo
        : replyTo?.id || replyTo?._id;

    return chatMessages.find(
      (message) => message?.id === replyId || message?._id === replyId
    ) || null;
  };

  const queryParameters = new URLSearchParams(window.location.search);
  const agent = queryParameters.get("agent");
  const partnerRoleSlug = queryParameters.get("roleSlug") || "";
  const userid = queryParameters.get("id");
  const groupId = queryParameters.get("groupId");
  const isGroupChat = queryParameters.get("group") === "true";
  const name = queryParameters.get("name");
  const profileImg = queryParameters.get("img");
  const time = queryParameters.get("topic") || queryParameters.get("time");
  const currentUserId = localStorage.getItem("userid");
  const baseUrl = `${window.location.protocol}//${window.location.host}`;

  const [groupMembers, setGroupMembers] = useState([]);

  useEffect(() => {
    if (!isGroupChat || !groupId) return;
    allApi(`${baseUrl}/api/chat-groups`, "get").then((resp) => {
      const group = (resp?.data || []).find((g) => g.id === groupId || g._id === groupId);
      setGroupMembers(group?.membersInfo || []);
    });
  }, [isGroupChat, groupId, baseUrl]);

  const filteredForwardUsers = forwardUsers.filter((user) => {
    const username = user?.username || `${user?.firstName || ""} ${user?.lastName || ""}`;
    return username.toLowerCase().includes(forwardSearch.toLowerCase());
  });
  const filteredForwardGroups = forwardGroups.filter((group) => {
    const groupName = `${group?.name || ""} ${group?.topic || ""}`.trim();
    return groupName.toLowerCase().includes(forwardSearch.toLowerCase());
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const [dbSearchMatches, setDbSearchMatches] = useState([]);
  const searchMatches = dbSearchMatches;

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const hasFetchedRef = useRef(false);
  const scrollSearchAbortRef = useRef(false);
  const isSearchScrollingRef = useRef(false);

  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  const prevMessageCountRef = useRef(0);
  const prevLastMessageIdRef = useRef(null);

  const activeSearchMatch =
    searchMatches.length > 0 ? searchMatches[activeSearchIndex] : null;
  const activeSearchMessageId = activeSearchMatch?.messageId || null;

  const formatTargetType = (roleName = "", roleSlug = "") => {
    if (roleName?.trim()) {
      return roleName.trim();
    }

    if (roleSlug === "team_leader") {
      return "Team Leader";
    }

    if (roleSlug === "branch_leader") {
      return "Branch Leader";
    }

    if (roleSlug === "customer") {
      return "User";
    }

    return "User";
  };

  const clearPendingAutoScrolls = () => {
    autoScrollTimeoutsRef.current.forEach((timeoutId) =>
      window.clearTimeout(timeoutId)
    );
    autoScrollTimeoutsRef.current = [];
  };

  const scrollToBottom = (behavior = "auto") => {
    const container = scrollableDivRef.current;

    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  };

  const scheduleScrollToBottom = (behavior = "auto") => {
    if (isSearchScrollingRef.current) return;
    clearPendingAutoScrolls();

    scrollToBottom(behavior);

    [0, 80, 220, 420].forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          scrollToBottom("auto");
        });
      }, delay);

      autoScrollTimeoutsRef.current.push(timeoutId);
    });
  };
  //notification
  //  useEffect(() => {
  //   if (Notification.permission !== "granted") {
  //     Notification.requestPermission();
  //   }
  // }, []);
  //   const showBrowserNotification = (title, body) => {
  //   if (Notification.permission === "granted") {
  //     const notification = new Notification(title, { body });
  //     setTimeout(() => notification.close(), 10000);
  //   }
  //   // Play sound
  //   const audio = new Audio(
  //     messageAudioNotification
  //   );
  //   audio.play().catch((e) => console.error("Audio play error:", e));
  // };
  useEffect(() => {
    if (!loading && chatMessages.length > 0) {
      const lastMsg = chatMessages[chatMessages.length - 1];
      const lastMsgId = lastMsg?.id || lastMsg?._id;
      // Only scroll when a genuinely new message appeared at the bottom,
      // not when older messages were prepended via "Syncing older messages"
      const isNewMessageAtBottom = lastMsgId !== prevLastMessageIdRef.current;
      if (isNewMessageAtBottom) {
        const lastSenderId =
          typeof lastMsg?.sender === "string"
            ? lastMsg.sender
            : lastMsg?.sender?.id || lastMsg?.sender?._id;
        const isOwnMessage = lastSenderId === currentUserId;
        if (isOwnMessage || !isScrolledUpRef.current) {
          scheduleScrollToBottom("auto");
        }
      }
      prevMessageCountRef.current = chatMessages.length;
      prevLastMessageIdRef.current = lastMsgId;
    }
  }, [chatMessages, loading]);

  useEffect(() => {
    return () => {
      clearPendingAutoScrolls();
    };
  }, []);

  useEffect(() => {
    const container = scrollableDivRef.current;
    if (!container) return;
    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      const scrolledUp = distanceFromBottom > 150;
      setShowScrollBtn(scrolledUp);
      isScrolledUpRef.current = scrolledUp;
      if (distanceFromBottom < 50) setReturnMessageId(null);
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [loading]);

  // Pre-fill search bar when navigating from chat-users message match
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchQ = params.get("searchQ");
    if (searchQ) {
      setShowSearch(true);
      setSearchQuery(searchQ);
    }
  }, []);

  useEffect(() => {
    if (!normalizedSearchQuery) {
      setActiveSearchIndex(0);
      return;
    }

    setActiveSearchIndex(0);
  }, [normalizedSearchQuery]);

  useEffect(() => {
    if (!searchMatches.length) {
      return;
    }

    if (activeSearchIndex > searchMatches.length - 1) {
      setActiveSearchIndex(searchMatches.length - 1);
    }
  }, [activeSearchIndex, searchMatches]);

  // Reset fetch flag when the chat target changes (different user/group)
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [groupId, isGroupChat, userid]);

  // First 50 messages — guarded so reconnects don't re-fetch from scratch
  useEffect(() => {
    const fetchInitialMessages = async () => {
      try {
        const responsePayload = isGroupChat
          ? { groupid: groupId, page: 1 }
          : { userid, page: 1 };
        const response = await socketEmit("older_messages", {
          ...responsePayload,
        });
        console.log("✅ older_messages", response);
        if (response?.data?.length) {
          setChatMessages(response.data);
          setPage(2); // next page
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Initial fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    if (socketReady && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchInitialMessages();
    }
  }, [groupId, isGroupChat, socketReady, userid]);

  // Load more older messages
  const loadMoreMessages = async () => {
    if (!hasMore) return;

    const div = scrollableDivRef.current;
    const prevScrollHeight = div?.scrollHeight;

    setLoadingMore(true);
    try {
      const responsePayload = isGroupChat
        ? { groupid: groupId, page: pageRef.current }
        : { userid, page: pageRef.current };
      const response = await socketEmit("older_messages", responsePayload);
      if (response?.data?.length) {
        setChatMessages((prev) => [...response.data, ...prev]);
        setPage((prev) => prev + 1);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Load more error:", err);
    } finally {
      setLoadingMore(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (div) {
            div.scrollTop = div.scrollHeight - prevScrollHeight;
          }
        });
      });
    }
  };

  // Handle new incoming message
  useEffect(() => {
    const handleNewMessage = (response) => {
      console.log("response", response);
      //  showBrowserNotification("New Message", response.content);
      const senderId =
        typeof response?.sender === "string"
          ? response?.sender
          : response?.sender?.id || response?.sender?._id;

      if (
        (isGroupChat && response?.chatGroup === groupId) ||
        (!isGroupChat && userid === senderId)
      ) {
        setChatMessages((prev) => {
          const messageExists = prev.some((item) => item?.id === response?.id);
          if (messageExists) {
            return prev;
          }

          const updated = [...prev, response];
          localStorage.setItem("chatMessages", JSON.stringify(updated));
          return updated;
        });
      }
    };
    socketOn("new_message", handleNewMessage);
    return () => socket.off("new_message", handleNewMessage);
  }, [groupId, isGroupChat, userid]);

  // Handle reaction updates from server
  useEffect(() => {
    const handleReactionUpdate = (data) => {
      const { messageId, reactions } = data;
      setChatMessages((prev) =>
        prev.map((m) => (m.id === messageId || m._id === messageId) ? { ...m, reactions } : m)
      );
    };
    socketOn("reaction_update", handleReactionUpdate);
    return () => socket.off("reaction_update", handleReactionUpdate);
  }, []);

  const REACTION_EMOJIS = ["😍", "🤨", "✅", "❌", "❤️"];

  const handleReaction = async (messageId, emoji) => {
    setReactionPickerMsgId(null);
    try {
      await socketEmit("react_message", { messageId, emoji });
    } catch (err) {
      console.error("Reaction error:", err);
    }
  };

  const aggregateReactions = (reactions) => {
    const map = {};
    (reactions || []).forEach((r) => {
      map[r.emoji] = (map[r.emoji] || 0) + 1;
    });
    return Object.entries(map);
  };

  const myReactionEmoji = (reactions) => {
    const mine = (reactions || []).find(
      (r) => r.userId === currentUserId || r.userId?.toString() === currentUserId
    );
    return mine?.emoji || null;
  };

  const scrollToReplyMessage = (messageId) => {
    const elementId = `chat-message-${messageId}`;
    const container = scrollableDivRef.current;
    const el = document.getElementById(elementId);
    if (!el || !container) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const targetScrollTop =
      container.scrollTop +
      (elRect.top - containerRect.top) -
      container.clientHeight / 2 +
      el.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
    setHighlightedMessageId(messageId);
    window.clearTimeout(window.__chatReplyHighlightTimer);
    window.__chatReplyHighlightTimer = window.setTimeout(() => {
      setHighlightedMessageId((cur) => (cur === messageId ? null : cur));
    }, 1800);
  };

  const jumpToMessage = (messageId, returnId = null) => {
    if (!messageId) return;
    jumpAbortRef.current = false;
    if (returnId) setReturnMessageId(returnId);
    setJumpTargetMessageId(messageId);
  };

  const moveSearchSelection = (direction) => {
    if (!searchMatches.length) {
      return;
    }

    setActiveSearchIndex((currentIndex) => {
      if (direction === "prev") {
        return currentIndex === 0 ? searchMatches.length - 1 : currentIndex - 1;
      }

      return currentIndex === searchMatches.length - 1 ? 0 : currentIndex + 1;
    });
  };

  const openForwardModal = async (messages) => {
    const msgsArray = Array.isArray(messages) ? messages : [messages];
    setForwardMessages(msgsArray);
    setForwardSearch("");
    setForwardLoading(true);

    try {
      const [response, groupsResponse] = await Promise.all([
        socketEmit("users_chat_with_me", {
          agentask: 2,
          page: 1,
        }),
        allApi(`${baseUrl}/api/chat-groups`, "get"),
      ]);

      const fetchedUsers = response?.data || [];
      const fetchedGroups = groupsResponse?.data || [];
      const currentChatExists = fetchedUsers.some(
        (user) => user?.userId === userid || user?.sender === userid
      );

      if (!currentChatExists && userid) {
        fetchedUsers.unshift({
          userId: userid,
          username: name || "Current chat",
          profile: profileImg || "",
          lastSeen: time || "",
        });
      }

      setForwardUsers(fetchedUsers);

      const eligibleGroups = fetchedGroups.filter((group) => {
        if (!group?.id) {
          return false;
        }

        if (isGroupChat) {
          return group.id !== groupId;
        }

        return true;
      });

      setForwardGroups(eligibleGroups);
    } catch (error) {
      console.error("Forward users fetch error:", error);
      setForwardUsers([]);
      setForwardGroups([]);
    } finally {
      setForwardLoading(false);
    }
  };

  const closeForwardModal = () => {
    setForwardMessages([]);
    setForwardUsers([]);
    setForwardGroups([]);
    setForwardSearch("");
    setForwardingTargetKey(null);
    exitSelectionMode();
  };

  const forwardCurrentMessage = async (target) => {
    if (!forwardMessages.length || forwardingTargetKey) {
      return;
    }

    const targetKey = target?.type === "group" ? `group-${target?.groupId}` : `user-${target?.userId}`;

    if (!targetKey || (!target?.userId && !target?.groupId)) {
      return;
    }

    setForwardingTargetKey(targetKey);

    try {
      for (const msg of forwardMessages) {
        const payload = {
          messageType: msg?.messageType || "text",
          content: msg?.content || "",
        };

        if (target?.type === "group") {
          payload.groupid = target.groupId;
        } else {
          payload.touserid = target.userId;
          payload.recipient = target.userId;
        }

        const fileIds = (msg?.files || [])
          .map((file) => file?.id || file?._id)
          .filter(Boolean);

        if (fileIds.length > 0) {
          payload.attachments = fileIds;
        }

        await socketEmit("send_message", payload);
      }
      closeForwardModal();
    } catch (error) {
      console.error("Forward message error:", error);
    } finally {
      setForwardingTargetKey(null);
    }
  };
  //mobile phone keyboard height
  useEffect(() => {
    const initialHeight = window.innerHeight;
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDiff = initialHeight - currentHeight;
      // If keyboard is likely open (height reduced by >100px)
      if (heightDiff > 100) {
        setChatHeight("10vh");
      } else {
        setChatHeight("100%");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounced DB search — searches all messages in DB, not just loaded ones
  useEffect(() => {
    if (!normalizedSearchQuery) {
      setDbSearchMatches([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const body = isGroupChat
          ? { groupId, searchQuery: normalizedSearchQuery }
          : { secondUserId: userid, searchQuery: normalizedSearchQuery };
        const result = await postApi(`${baseUrl}/api/messages/search-chat`, 'post', body);
        const msgs = result?.data || [];
        setDbSearchMatches(msgs.map(m => ({
          messageId: String(m._id || m.id || ''),
          content: m.content || '',
        })));
        setActiveSearchIndex(msgs.length > 0 ? msgs.length - 1 : 0);
      } catch (err) {
        console.error('Chat search error:', err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [normalizedSearchQuery, isGroupChat, groupId, userid, baseUrl]);

  const scrollToSearchMessage = (messageId) => {
    const elementId = `chat-message-${messageId}`;
    const container = scrollableDivRef.current;
    const el = document.getElementById(elementId);
    if (!el || !container) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const targetScrollTop =
      container.scrollTop +
      (elRect.top - containerRect.top) -
      container.clientHeight / 2 +
      el.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
    setHighlightedMessageId(messageId);
    window.clearTimeout(window.__chatSearchHighlightTimer);
    window.__chatSearchHighlightTimer = window.setTimeout(() => {
      setHighlightedMessageId((cur) => (cur === messageId ? null : cur));
      isSearchScrollingRef.current = false;
    }, 2000);
  };

  // Scroll to active match; auto-load older pages if message not yet in DOM
  useEffect(() => {
    if (!activeSearchMessageId) return;

    const elementId = `chat-message-${activeSearchMessageId}`;
    if (document.getElementById(elementId)) {
      isSearchScrollingRef.current = true;
      clearPendingAutoScrolls();
      setTimeout(() => scrollToSearchMessage(activeSearchMessageId), 50);
      return;
    }

    isSearchScrollingRef.current = true;
    scrollSearchAbortRef.current = false;
    (async () => {
      for (let i = 0; i < 30; i++) {
        if (scrollSearchAbortRef.current || !hasMoreRef.current) break;
        setLoadingMore(true);
        try {
          const responsePayload = isGroupChat
            ? { groupid: groupId, page: pageRef.current }
            : { userid, page: pageRef.current };
          const response = await socketEmit('older_messages', responsePayload);
          if (response?.data?.length) {
            setChatMessages(prev => [...response.data, ...prev]);
            setPage(prev => prev + 1);
          } else {
            setHasMore(false);
            hasMoreRef.current = false;
            break;
          }
        } catch { break; } finally { setLoadingMore(false); }

        await new Promise(r => setTimeout(r, 150));
        if (scrollSearchAbortRef.current) break;

        if (document.getElementById(elementId)) {
          clearPendingAutoScrolls();
          setTimeout(() => scrollToSearchMessage(activeSearchMessageId), 100);
          return;
        }
      }
      isSearchScrollingRef.current = false;
    })();

    return () => {
      scrollSearchAbortRef.current = true;
      isSearchScrollingRef.current = false;
    };
  }, [activeSearchMessageId, isGroupChat, groupId, userid]);

  // Jump to replied message — loads older pages if not yet in DOM
  useEffect(() => {
    if (!jumpTargetMessageId) return;

    const elementId = `chat-message-${jumpTargetMessageId}`;

    if (document.getElementById(elementId)) {
      clearPendingAutoScrolls();
      scrollToReplyMessage(jumpTargetMessageId);
      setJumpTargetMessageId(null);
      return;
    }

    jumpAbortRef.current = false;
    (async () => {
      for (let i = 0; i < 30; i++) {
        if (jumpAbortRef.current || !hasMoreRef.current) break;
        setLoadingMore(true);
        try {
          const responsePayload = isGroupChat
            ? { groupid: groupId, page: pageRef.current }
            : { userid, page: pageRef.current };
          const response = await socketEmit('older_messages', responsePayload);
          if (response?.data?.length) {
            setChatMessages(prev => [...response.data, ...prev]);
            setPage(prev => prev + 1);
          } else {
            setHasMore(false);
            hasMoreRef.current = false;
            break;
          }
        } catch { break; } finally { setLoadingMore(false); }

        await new Promise(r => setTimeout(r, 150));
        if (jumpAbortRef.current) break;

        if (document.getElementById(elementId)) {
          clearPendingAutoScrolls();
          setTimeout(() => {
            scrollToReplyMessage(jumpTargetMessageId);
            setJumpTargetMessageId(null);
          }, 100);
          return;
        }
      }
      setJumpTargetMessageId(null);
    })();

    return () => { jumpAbortRef.current = true; };
  }, [jumpTargetMessageId, isGroupChat, groupId, userid]);

  return (
    <>
      <MobileHeader
        logo={profileImg || logo}
        name={name}
        time={time}
        agent={agent}
        showSearch={showSearch}
        onSearchToggle={toggleSearch}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchCount={normalizedSearchQuery ? `${searchMatches.length ? activeSearchIndex + 1 : 0}/${searchMatches.length}` : "0/0"}
        searchMatchesLength={searchMatches.length}
        onSearchPrev={() => moveSearchSelection("prev")}
        onSearchNext={() => moveSearchSelection("next")}
        customerId={!isGroupChat ? userid : null}
        partnerRoleSlug={partnerRoleSlug}
      />
      <div className="container-fluid mobile-bg">
        <div
          className="ps-5 pe-5 mbl-pedding-10 chat-screen-padding"
          ref={scrollableDivRef}
          style={{ overflowY: "auto", maxHeight: chatHeight }}
        >
          {loading ? (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="grow" />
            </div>
          ) : (
            <>
              {hasMore && (
                <div className="text-center mb-3">
                  <button
                    className="btn btn-sm"
                    onClick={loadMoreMessages}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      "Loading..."
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat"></i> Syncing older
                        messages. Click Here
                      </>
                    )}
                  </button>
                </div>
              )}

              {chatMessages.map((msg, index) => {
                const messageKey = msg?.id || msg?._id;
                const linkedReplyMessage = resolveReplyMessage(msg?.replyTo);
                const senderId =
                  typeof msg?.sender === "string"
                    ? msg?.sender
                    : msg?.sender?.id || msg?.sender?._id;
                const isOwnMessage = senderId === currentUserId;
                const isStickerMessage =
                  msg?.messageType === "sticker" && msg?.files?.length > 0;
                const showDate =
                  index === 0 ||
                  !moment(msg.createdAt).isSame(
                    chatMessages[index - 1]?.createdAt,
                    "day"
                  );

                return (
                  <React.Fragment key={index}>
                    {showDate && (
                      <Row className="justify-content-center">
                        <div className="text-center my-2">
                          <small className="text-muted text-secondary">
                            {moment(msg.createdAt).format("D MMMM")}
                          </small>
                        </div>
                      </Row>
                    )}

                    {!isOwnMessage ? (
                      <Row className={`mt-1 align-items-center ${isSelectionMode ? "chat-select-row" : ""}`}>
                        {isSelectionMode && (
                          <div className="chat-select-check-col" onClick={() => toggleMessageSelection(msg)}>
                            <i className={`bi ${isMessageSelected(msg) ? "bi-check-circle-fill text-primary" : "bi-circle text-secondary"} chat-select-check-icon`}></i>
                          </div>
                        )}
                        <div className="px-0" onClick={isSelectionMode ? () => toggleMessageSelection(msg) : undefined} style={isSelectionMode ? { cursor: "pointer" } : {}}>
                          <div
                            id={`chat-message-${messageKey}`}
                            className={`reciver-message m-w-80 bg-light-gray mb-2 ${
                              highlightedMessageId === messageKey || activeSearchMessageId === messageKey
                                ? "chat-message-highlight"
                                : ""
                            } ${isMessageSelected(msg) ? "chat-message-selected" : ""}`}
                          >
                            {!isSelectionMode && (
                              <div className="chat-message-actions">
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => setReplyMessage(msg)}
                                  aria-label="Reply to message"
                                  title="Reply"
                                >
                                  <i className="bi bi-reply"></i>
                                </button>
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => openForwardModal(msg)}
                                  aria-label="Forward message"
                                  title="Forward"
                                >
                                  <i className="bi bi-forward"></i>
                                </button>
                                {msg?.content?.trim() ? (
                                  <button
                                    type="button"
                                    className="chat-message-action-btn"
                                    onClick={() => copyMessageText(msg)}
                                    aria-label="Copy message"
                                    title="Copy"
                                  >
                                    <i className={`bi ${copiedMessageId === messageKey ? "bi-check2" : "bi-clipboard"}`}></i>
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => enterSelectionMode(msg)}
                                  aria-label="Select message"
                                  title="Select"
                                >
                                  <i className="bi bi-check-square"></i>
                                </button>
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => setReactionPickerMsgId(reactionPickerMsgId === messageKey ? null : messageKey)}
                                  aria-label="React"
                                  title="React"
                                >
                                  <i className="bi bi-emoji-smile"></i>
                                </button>
                              </div>
                            )}
                            {reactionPickerMsgId === messageKey && (
                              <div className="chat-reaction-picker">
                                {REACTION_EMOJIS.map((em) => (
                                  <button key={em} className={`chat-reaction-pick-btn ${myReactionEmoji(msg.reactions) === em ? "active" : ""}`} onClick={() => handleReaction(messageKey, em)}>{em}</button>
                                ))}
                              </div>
                            )}
                            <ReplyPreview
                              replyTo={linkedReplyMessage}
                              title="Replied message"
                              onClick={() =>
                                jumpToMessage(linkedReplyMessage?.id || linkedReplyMessage?._id, messageKey)
                              }
                              className="chat-message-reply"
                            />
                            {msg?.files?.length > 0 && (
                              <Row className={isStickerMessage ? "chat-sticker-stack" : ""}>
                                {msg.files.map((src, idx) => (
                                  <Col className="text-center" key={idx}>
                                    {isImage(src?.cloudUrl) && (
                                      <ShowImage
                                        img={src?.cloudUrl}
                                        alt={src?.fileName}
                                        width={isStickerMessage ? "chat-sticker-img" : "chat-img"}
                                        onLoad={() => { if (!isScrolledUpRef.current) scheduleScrollToBottom("auto"); }}
                                      />
                                    )}
                                    {isAudio(src?.cloudUrl) && (
                                      <audio
                                        controls
                                        preload="metadata"
                                        className="chat-audio-player"
                                        onLoadedMetadata={() => { if (!isScrolledUpRef.current) scheduleScrollToBottom("auto"); }}
                                      >
                                        <source
                                          src={src?.cloudUrl}
                                          type={getAudioMimeType(src?.cloudUrl, src?.fileType)}
                                        />
                                      </audio>
                                    )}
                                    {!isImage(src?.cloudUrl) && !isAudio(src?.cloudUrl) && src?.cloudUrl && (
                                      <a href={src.cloudUrl} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 p-2 border rounded text-decoration-none text-dark">
                                        <i className="bi bi-file-earmark-fill fs-4 text-secondary"></i>
                                        <span className="text-truncate" style={{ maxWidth: 140, fontSize: 13 }}>{src.fileName || "Download file"}</span>
                                      </a>
                                    )}
                                  </Col>
                                ))}
                              </Row>
                            )}
                            <p className={`mb-0 ${isStickerMessage ? "chat-sticker-caption" : ""}`}>
                              {!isStickerMessage && isGroupChat && msg?.senderName && (
                                <span className="d-block fw-bold mb-1">
                                  {msg?.senderName}
                                </span>
                              )}
                              {msg?.content?.trim() ? (
                                <LinkifiedText text={msg?.content} highlight={searchQuery} />
                              ) : null}
                              {msg?.content?.trim() ? <br /> : null}
                              <span className="m-f-13 float-left">
                                {msg?.updatedMessageAt ? "Edited " : ""}{moment(msg.createdAt).format("hh:mm A")}
                              </span>
                            </p>
                            {msg?.reactions?.length > 0 && (
                              <div className="chat-reactions-row">
                                {aggregateReactions(msg.reactions).map(([em, count]) => (
                                  <button key={em} className={`chat-reaction-bubble ${myReactionEmoji(msg.reactions) === em ? "active" : ""}`} onClick={() => handleReaction(messageKey, em)}>
                                    {em}{count > 1 && <span>{count}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </Row>
                    ) : (
                      <Row className={`mt-1 align-items-center ${isSelectionMode ? "chat-select-row chat-select-row-own" : ""}`}>
                        {isSelectionMode && (
                          <div className="chat-select-check-col" onClick={() => toggleMessageSelection(msg)}>
                            <i className={`bi ${isMessageSelected(msg) ? "bi-check-circle-fill text-primary" : "bi-circle text-secondary"} chat-select-check-icon`}></i>
                          </div>
                        )}
                        <div className="px-0" onClick={isSelectionMode ? () => toggleMessageSelection(msg) : undefined} style={isSelectionMode ? { cursor: "pointer" } : {}}>
                          <div
                            id={`chat-message-${messageKey}`}
                            className={`message m-w-80 mb-2 ${
                              highlightedMessageId === messageKey || activeSearchMessageId === messageKey
                                ? "chat-message-highlight"
                                : ""
                            } ${isMessageSelected(msg) ? "chat-message-selected" : ""}`}
                          >
                            {!isSelectionMode && (
                              <div className="chat-message-actions">
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => setReplyMessage(msg)}
                                  aria-label="Reply to message"
                                  title="Reply"
                                >
                                  <i className="bi bi-reply"></i>
                                </button>
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => openForwardModal(msg)}
                                  aria-label="Forward message"
                                  title="Forward"
                                >
                                  <i className="bi bi-forward"></i>
                                </button>
                                {msg?.content?.trim() ? (
                                  <button
                                    type="button"
                                    className="chat-message-action-btn"
                                    onClick={() => copyMessageText(msg)}
                                    aria-label="Copy message"
                                    title="Copy"
                                  >
                                    <i className={`bi ${copiedMessageId === messageKey ? "bi-check2" : "bi-clipboard"}`}></i>
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => enterSelectionMode(msg)}
                                  aria-label="Select message"
                                  title="Select"
                                >
                                  <i className="bi bi-check-square"></i>
                                </button>
                                <button
                                  type="button"
                                  className="chat-message-action-btn"
                                  onClick={() => setReactionPickerMsgId(reactionPickerMsgId === messageKey ? null : messageKey)}
                                  aria-label="React"
                                  title="React"
                                >
                                  <i className="bi bi-emoji-smile"></i>
                                </button>
                              </div>
                            )}
                            {reactionPickerMsgId === messageKey && (
                              <div className="chat-reaction-picker">
                                {REACTION_EMOJIS.map((em) => (
                                  <button key={em} className={`chat-reaction-pick-btn ${myReactionEmoji(msg.reactions) === em ? "active" : ""}`} onClick={() => handleReaction(messageKey, em)}>{em}</button>
                                ))}
                              </div>
                            )}
                            <ReplyPreview
                              replyTo={linkedReplyMessage}
                              title="Replied message"
                              isOwnMessage
                              onClick={() =>
                                jumpToMessage(linkedReplyMessage?.id || linkedReplyMessage?._id, messageKey)
                              }
                              className="chat-message-reply"
                            />
                            {msg?.files?.length > 0 && (
                              <Row className={isStickerMessage ? "chat-sticker-stack" : ""}>
                                {msg.files.map((src, idx) => (
                                  <Col className="text-center" key={idx}>
                                    {isImage(src?.cloudUrl) ? (
                                      <ShowImage
                                        img={src?.cloudUrl}
                                        alt={src?.fileName}
                                        width={isStickerMessage ? "chat-sticker-img" : "chat-img"}
                                        onLoad={() => { if (!isScrolledUpRef.current) scheduleScrollToBottom("auto"); }}
                                      />
                                    ) :  isAudio(src?.cloudUrl) ? (
                                      <audio
                                        controls
                                        preload="metadata"
                                        className="chat-audio-player"
                                        onLoadedMetadata={() => { if (!isScrolledUpRef.current) scheduleScrollToBottom("auto"); }}
                                      >
                                        <source
                                          src={src?.cloudUrl}
                                          type={getAudioMimeType(src?.cloudUrl, src?.fileType)}
                                        />
                                      </audio>
                                    ) : src?.cloudUrl ? (
                                      <a href={src.cloudUrl} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 p-2 border rounded text-decoration-none text-dark">
                                        <i className="bi bi-file-earmark-fill fs-4 text-secondary"></i>
                                        <span className="text-truncate" style={{ maxWidth: 140, fontSize: 13 }}>{src.fileName || "Download file"}</span>
                                      </a>
                                    ) : null}
                                  </Col>
                                ))}
                              </Row>
                            )}
                            <p className={`mb-0 ${isStickerMessage ? "chat-sticker-caption" : ""}`}>
                              {msg?.content?.trim() ? (
                                <LinkifiedText text={msg?.content} highlight={searchQuery} />
                              ) : null}{" "}
                              {!isStickerMessage && moment().diff(moment(msg.createdAt), "minutes") < 5 && <EditMsg setChatMessages={setChatMessages} chatMessages={chatMessages} msg={msg?.content} id={msg?.id}
                              messageType={msg?.messageType}
                              recipient={msg?.recipient}
                              sender={msg?.sender}/>}
                              {msg?.content?.trim() ? <br /> : null}
                              <span className="m-f-13 float-right">
                                {msg?.updatedMessageAt ? "Edited " : ""}{moment(msg.createdAt).format("hh:mm A")}{" "}
                                <i
                                  className={`bi bi-check2-all ${
                                    msg?.isRead ? "text-primary" : ""
                                  }`}
                                ></i>
                              </span>
                            </p>
                            {msg?.reactions?.length > 0 && (
                              <div className="chat-reactions-row chat-reactions-row-own">
                                {aggregateReactions(msg.reactions).map(([em, count]) => (
                                  <button key={em} className={`chat-reaction-bubble ${myReactionEmoji(msg.reactions) === em ? "active" : ""}`} onClick={() => handleReaction(messageKey, em)}>
                                    {em}{count > 1 && <span>{count}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </Row>
                    )}
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {showScrollBtn && (
        <button
          className={`chat-scroll-to-bottom-btn ${returnMessageId ? "chat-scroll-return" : ""}`}
          onClick={() => {
            if (returnMessageId) {
              const id = returnMessageId;
              setReturnMessageId(null);
              scrollToReplyMessage(id);
            } else {
              scheduleScrollToBottom("smooth");
            }
          }}
          aria-label={returnMessageId ? "Return to message" : "Scroll to bottom"}
        >
          <i className={`bi ${returnMessageId ? "bi-arrow-return-right" : "bi-chevron-down"}`}></i>
        </button>
      )}

      <div className="chat-bottom px-1">
        {isSelectionMode ? (
          <div className="chat-selection-bar">
            <button className="chat-selection-cancel" onClick={exitSelectionMode}>
              <i className="bi bi-x-lg me-1"></i> Cancel
            </button>
            <span className="chat-selection-count">
              {selectedMessages.length} selected
            </span>
            <button
              className="chat-selection-forward"
              onClick={() => openForwardModal(selectedMessages)}
              disabled={!selectedMessages.length}
            >
              <i className="bi bi-forward me-1"></i> Forward
            </button>
          </div>
        ) : (
          <ChatInput
            id={userid}
            groupId={groupId}
            isGroupChat={isGroupChat}
            chatMessages={chatMessages}
            setChatMessages={setChatMessages}
            replyMessage={replyMessage}
            setReplyMessage={setReplyMessage}
            socketReady={socketReady}
            mentionMembers={groupMembers}
          />
        )}
      </div>

      <Footer />

      <Modal show={forwardMessages.length > 0} onHide={closeForwardModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {forwardMessages.length > 1 ? `Forward ${forwardMessages.length} Messages` : "Forward Message"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="chat-forward-preview">
            <p className="mb-1 fw-bold">
              {forwardMessages.length > 1 ? `${forwardMessages.length} messages selected` : "Selected message"}
            </p>
            {forwardMessages.length === 1 ? (
              <p className="mb-0 text-break">
                {forwardMessages[0]?.content || (forwardMessages[0]?.files?.length ? "Attachment" : "Message")}
              </p>
            ) : (
              <div>
                {forwardMessages.slice(0, 3).map((m, i) => (
                  <p key={i} className="mb-1 text-break text-muted small">
                    • {m?.content || (m?.files?.length ? "Attachment" : "Message")}
                  </p>
                ))}
                {forwardMessages.length > 3 && (
                  <p className="mb-0 text-muted small">...and {forwardMessages.length - 3} more</p>
                )}
              </div>
            )}
          </div>

          <Form.Control
            className="mb-3"
            placeholder="Search user or group..."
            value={forwardSearch}
            onChange={(event) => setForwardSearch(event.target.value)}
          />

          {forwardLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : filteredForwardUsers.length > 0 || filteredForwardGroups.length > 0 ? (
            <>
              {filteredForwardGroups.length > 0 && (
                <>
                  <p className="mb-2 fw-bold">Groups</p>
                  <div className="chat-forward-users mb-3">
                    {filteredForwardGroups.map((group, index) => {
                      const isForwarding = forwardingTargetKey === `group-${group?.id}`;
                      const groupLabel = group?.topic?.trim()
                        ? group.topic
                        : isGroupChat
                        ? "Tap to forward"
                        : "Group you can forward to";

                      return (
                        <button
                          key={`${group?.id}-${index}`}
                          type="button"
                          className="chat-forward-user"
                          onClick={() =>
                            forwardCurrentMessage({
                              type: "group",
                              groupId: group?.id,
                            })
                          }
                          disabled={isForwarding}
                        >
                          {group?.image ? (
                            <img
                              className="profile-img"
                              src={group.image}
                              alt={group?.name || "Group"}
                            />
                          ) : (
                            <div className="profile-img d-flex align-items-center justify-content-center bg-success text-white fw-bold">
                              {(group?.name || "G").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="chat-forward-user-copy">
                            <strong>{group?.name || "Group"}</strong>
                            <span>{`Group • ${groupLabel}`}</span>
                          </div>
                          {isForwarding ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <i className="bi bi-send"></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {filteredForwardUsers.length > 0 && (
                <>
                  <p className="mb-2 fw-bold">Users</p>
                  <div className="chat-forward-users">
                    {filteredForwardUsers.map((user, index) => {
                      const userId = user?.userId || user?.sender;
                      const username =
                        user?.username ||
                        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                        "New User";
                      const isForwarding = forwardingTargetKey === `user-${userId}`;

                      return (
                        <button
                          key={`${userId}-${index}`}
                          type="button"
                          className="chat-forward-user"
                          onClick={() => forwardCurrentMessage({ ...user, type: "user", userId })}
                          disabled={isForwarding}
                        >
                          <img
                            className="profile-img"
                            src={user?.profile || logo}
                            alt={username}
                            style={!user?.profile ? { objectFit: "contain", background: "#fff" } : undefined}
                          />
                          <div className="chat-forward-user-copy">
                            <strong>{username}</strong>
                            <span>{formatTargetType(user?.roleName, user?.roleSlug)}</span>
                          </div>
                          {isForwarding ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <i className="bi bi-send"></i>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="mb-0 text-muted">No users or eligible groups found.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeForwardModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Chat;
